import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * BankDetailsCard — Admin-facing form on Organization Settings.
 *
 * Captures the org's bank account details, IBAN/BIC and a payment
 * reference prefix. These are shown to parents when they choose "Bank
 * Transfer" while paying for a PAYG booking. Only visible to org
 * owners / admins (route-level check on the backend, this component
 * doesn't need extra gating).
 */
const FIELDS = [
  { key: 'account_name',              label: 'Account name',              placeholder: 'e.g. Kids WiseApp Ltd', full: true },
  { key: 'sort_code',                 label: 'Sort code',                 placeholder: '12-34-56' },
  { key: 'account_number',            label: 'Account number',            placeholder: '12345678' },
  { key: 'iban',                      label: 'IBAN',                      placeholder: 'GB29 NWBK 6016 1331 9268 19', full: true },
  { key: 'bic',                       label: 'BIC / SWIFT',               placeholder: 'NWBKGB2L' },
  { key: 'payment_reference_prefix',  label: 'Payment reference prefix',  placeholder: 'KW (max 8 chars)' },
  { key: 'payment_instructions',      label: 'Payment instructions (optional)', textarea: true, full: true, placeholder: 'e.g. "Please include the booking reference in your transfer."' },
];

export function BankDetailsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api.getOrgBankDetails();
        if (!cancelled) setForm(data || {});
      } catch (err) {
        if (!cancelled) toast.error(err?.message || 'Failed to load bank details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.updateOrgBankDetails(form);
      setForm(updated || form);
      toast.success('Bank details saved.');
    } catch (err) {
      toast.error(err?.message || 'Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" data-testid="org-bank-details-card">
      <h2 style={{ marginTop: 0 }}>🏦 Bank Details (Parent Payments)</h2>
      <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem', marginBottom: '1rem' }}>
        Shown to parents who choose <strong>Bank Transfer</strong> as their booking payment method.
        Bookings sit as <em>pending</em> until you confirm receipt in the “Pending Payments” section.
      </p>
      {loading ? (
        <div className="loading" data-testid="org-bank-details-loading">Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem' }}>
          {FIELDS.map((f) => (
            <div
              key={f.key}
              className="form-group"
              style={f.full ? { gridColumn: '1 / -1' } : undefined}
            >
              <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>{f.label}</label>
              {f.textarea ? (
                <textarea
                  rows={3}
                  value={form[f.key] || ''}
                  onChange={handleChange(f.key)}
                  placeholder={f.placeholder}
                  maxLength={500}
                  data-testid={`org-bank-${f.key}`}
                  style={{ width: '100%', fontFamily: 'inherit', resize: 'vertical' }}
                />
              ) : (
                <input
                  type="text"
                  value={form[f.key] || ''}
                  onChange={handleChange(f.key)}
                  placeholder={f.placeholder}
                  maxLength={60}
                  data-testid={`org-bank-${f.key}`}
                  style={{ width: '100%' }}
                />
              )}
            </div>
          ))}
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
              data-testid="org-bank-save"
            >
              {saving ? 'Saving…' : 'Save bank details'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BankDetailsCard;
